import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, Globe, MapPin, Heart, Share, Star, Navigation } from "lucide-react";
import { useFavorites } from "@/context/FavoritesContext";
import { APIProvider, Map, AdvancedMarker } from "@vis.gl/react-google-maps";
import { analytics } from "@/utils/analytics";

const API_URL = '';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Soft, muted map style for detail page (Apple-inspired, less distracting)
const DETAIL_MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#f8f9fa" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "labels", stylers: [{ visibility: "simplified" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#f3f4f6" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#e5e7eb" }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#dbeafe" }] },
  { featureType: "water", elementType: "labels", stylers: [{ visibility: "off" }] },
];

// Custom location pin for the detail map
const LocationPin = () => (
  <div className="relative">
    <div className="w-10 h-10 rounded-full bg-teal-500 flex items-center justify-center shadow-lg shadow-teal-500/40 ring-4 ring-white">
      <MapPin className="w-5 h-5 text-white" fill="white" />
    </div>
  </div>
);

// Analytics tracking helper - now uses centralized utility
const trackEvent = (eventType, businessId) => {
  switch(eventType) {
    case 'listing_view':
    case 'profile_view':
      analytics.trackListingView(businessId);
      break;
    case 'call_click':
    case 'phone_call':
      analytics.trackCallClick(businessId);
      break;
    case 'website_click':
      analytics.trackWebsiteClick(businessId);
      break;
    case 'map_open':
      analytics.trackMapOpen(businessId);
      break;
    case 'direction_request':
      analytics.trackDirectionRequest(businessId);
      break;
    default:
      break;
  }
};

const FeaturedBadge = ({ isFeatured }) => {
  if (!isFeatured) return null;
  
  return (
    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase bg-amber-500 text-white">
      <Star className="w-3 h-3" />
      Featured
    </span>
  );
};

export default function BusinessDetailPage() {
  const { id } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isFavorite, toggleFavorite } = useFavorites();

  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/businesses/${id}`);
        if (response.ok) {
          const data = await response.json();
          setBusiness(data);
          // Track profile view
          trackEvent('profile_view', data.business_id);
        }
      } catch (error) {
        console.error('Failed to fetch business:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBusiness();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
        <p className="text-slate-500 font-body mb-4">Place not found</p>
        <Link to="/" className="text-teal-600 font-medium">
          Go back home
        </Link>
      </div>
    );
  }

  const saved = isFavorite(business.business_id);

  // All Core Listing users have access to all features (phone, website, map)
  const canCall = true;
  const canWebsite = !!business.website;
  const canMap = true;

  const handleCall = () => {
    trackEvent('phone_call', business.business_id);
    window.location.href = `tel:${business.phone}`;
  };

  const handleWebsite = () => {
    if (canWebsite && business.website) {
      trackEvent('website_click', business.business_id);
      window.open(business.website, '_blank', 'noopener,noreferrer');
    }
  };

  const handleMap = () => {
    trackEvent('direction_request', business.business_id);
    const query = encodeURIComponent(business.address);
    window.open(`https://maps.google.com/?q=${query}`, '_blank', 'noopener,noreferrer');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: business.name,
          text: business.description,
          url: window.location.href,
        });
      } catch (err) {
        console.debug('Share cancelled or failed:', err);
      }
    }
  };

  const handleFavorite = () => {
    toggleFavorite(business.business_id);
  };

  return (
    <div className="min-h-screen pb-24 bg-[#F2F2F7]" data-testid="business-detail-page">
      {/* Hero Image */}
      <div className="relative h-56 sm:h-64">
        <img
          src={business.image_url || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800'}
          alt={business.name}
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
        
        {/* Image credit */}
        {business.image_credit && (
          <div className="absolute bottom-2 right-2">
            <span className="text-[9px] text-white/60 font-body">
              Photo: {business.image_credit}
            </span>
          </div>
        )}
        
        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between safe-top">
          <Link 
            to={`/category/${business.category}`}
            className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm active:scale-95 transition-transform nav-icon-glow"
            aria-label="Back to category" data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 text-slate-700" />
          </Link>
          
          <div className="flex items-center gap-2">
            <button
              aria-label="Share this place" onClick={handleShare}
              className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm active:scale-95 transition-transform"
            >
              <Share className="w-5 h-5 text-slate-700" />
            </button>
            <button
              aria-label={saved ? "Unsave this place" : "Save this place"} aria-pressed={saved} onClick={handleFavorite}
              className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm active:scale-95 transition-transform"
              data-testid="favorite-button"
            >
              <Heart className={`w-5 h-5 ${saved ? 'fill-teal-600 text-teal-600' : 'text-slate-700'}`} />
            </button>
          </div>
        </div>

        {/* Featured Badge */}
        {business.is_featured && (
          <div className="absolute bottom-4 left-4">
            <FeaturedBadge isFeatured={business.is_featured} />
          </div>
        )}
      </div>

      {/* Content Card - Floating above map */}
      <div className="dq-detail-content relative z-10 mx-4 mb-4">
        <div className="bg-white rounded-3xl shadow-lg shadow-black/8 overflow-hidden">
          <div className="px-5 pt-6 pb-5">
            {/* Title */}
            <h1 className="text-2xl font-bold text-slate-900 font-heading mb-1">
              {business.name}
            </h1>
            
            {/* Category */}
            <p className="text-sm text-teal-600 font-medium capitalize mb-4">
              {business.category}
            </p>

            {/* Description */}
            <p className="text-sm text-slate-600 font-body leading-relaxed mb-5">
              {business.description}
            </p>

            {/* Details */}
            <div className="space-y-3">
              {/* Address */}
              <button 
                onClick={handleMap}
                className="w-full flex items-center gap-3 p-3 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-teal-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{business.address}</p>
                  <p className="text-xs text-teal-600 mt-0.5">Get directions →</p>
                </div>
              </button>

              {/* Phone */}
              <button 
                onClick={handleCall}
                className="w-full flex items-center gap-3 p-3 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{business.phone}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Tap to call</p>
                </div>
              </button>

              {/* Website */}
              {canWebsite && (
                <button 
                  onClick={handleWebsite}
                  className="w-full flex items-center gap-3 p-3 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{business.website?.replace(/^https?:\/\//, '')}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Visit website</p>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Embedded Map Section */}
      {GOOGLE_MAPS_API_KEY && business.latitude && business.longitude ? (
        <div className="relative mx-4 mb-28">
          <div className="rounded-3xl overflow-hidden shadow-lg shadow-black/8">
            {/* Map Container */}
            <div className="relative h-48 sm:h-56">
              <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                <Map
                  center={{ lat: business.latitude, lng: business.longitude }}
                  zoom={15}
                  gestureHandling="cooperative"
                  disableDefaultUI={true}
                  clickableIcons={false}
                  styles={DETAIL_MAP_STYLES}
                  mapId="detail-map"
                  className="w-full h-full"
                >
                  <AdvancedMarker
                    position={{ lat: business.latitude, lng: business.longitude }}
                  >
                    <LocationPin />
                  </AdvancedMarker>
                </Map>
              </APIProvider>
              
              {/* Soft overlay for premium feel */}
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-white/10 via-transparent to-white/5" />
            </div>
            
            {/* Map Footer - Open in Maps button */}
            <button
              onClick={handleMap}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-white border-t border-slate-100 text-teal-600 font-medium text-sm hover:bg-slate-50 transition-colors"
            >
              <Navigation className="w-4 h-4" />
              Open in Maps
            </button>
          </div>
        </div>
      ) : (
        /* Fallback when no coordinates or no API key */
        <div className="mx-4 mb-28">
          <button
            onClick={handleMap}
            className="w-full flex items-center justify-center gap-2 py-4 rounded-3xl bg-white shadow-lg shadow-black/8 text-teal-600 font-medium text-sm hover:bg-slate-50 transition-colors"
          >
            <Navigation className="w-4 h-4" />
            View on Google Maps
          </button>
        </div>
      )}

      {/* Fixed Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-t border-slate-100/80 layer-floating safe-bottom">
        <div className="max-w-lg mx-auto px-4 py-3 pb-safe flex gap-2">
          {/* Call Button */}
          <button
            onClick={handleCall}
            className="flex-1 h-12 rounded-full bg-teal-600 text-white font-semibold text-sm flex items-center justify-center gap-2 active:bg-teal-700 transition-colors"
            data-testid="call-button"
          >
            <Phone className="w-4 h-4" />
            Call
          </button>

          {/* Website Button */}
          {canWebsite && (
            <button
              onClick={handleWebsite}
              className="flex-1 h-12 rounded-full bg-white border border-slate-200 text-slate-700 font-semibold text-sm flex items-center justify-center gap-2 active:bg-slate-50 transition-colors"
              data-testid="website-button"
            >
              <Globe className="w-4 h-4" />
              Website
            </button>
          )}

          {/* Map Button */}
          <button
            onClick={handleMap}
            className="flex-1 h-12 rounded-full bg-white border border-slate-200 text-slate-700 font-semibold text-sm flex items-center justify-center gap-2 active:bg-slate-50 transition-colors"
            data-testid="map-button"
          >
            <MapPin className="w-4 h-4" />
            Map
          </button>
        </div>
      </div>
    </div>
  );
}
